/**
 * The posts that pulled the campaign's score down most in the window, worst
 * first as the route ranked them, each one a way into that post.
 */

import { Link } from '@tanstack/react-router';
import { ChevronRight, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';

import { QueryStates } from '../../../chrome/state';
import { IconHint } from '../../../chrome/hint';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { useRankedPosts, type Window } from '../api/queries';
import type { RankedPostRow } from '../api/schemas';
import { publicationPath, publicationsPath } from '../publications/paths';
import { st2 } from './messages';
import { commentsLabel, ScoreChip } from './RegionCard';
import { SentimentCard } from './SentimentCard';

const LISTED_POSTS = 3;

function nameOf(post: RankedPostRow): string | null {
  return post.label ?? post.handle;
}

/** The first letters of the first two words, skipping connectives such as
 *  "de" so "Prefeitura de Maceió" reads PM. */
function initialsOf(name: string): string {
  const words = name
    .replace(/^@/, '')
    .split(/[\s_]+/)
    .filter((word) => word !== '');
  const significant = words.filter((word) => word.length > 2);
  return (significant.length > 0 ? significant : words)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

export function PostScoresView(props: {
  campaignId: string;
  rows: readonly RankedPostRow[];
}): ReactNode {
  return (
    <ul className="flex flex-col gap-2">
      {props.rows.map((post) => {
        const name = nameOf(post);
        const shown = name ?? st2('posts.unnamed');
        return (
          <li
            key={post.post_id}
            aria-label={shown}
            className="flex items-center gap-3 rounded-lg border px-3 py-2"
          >
            <Avatar>
              <AvatarFallback>
                {name === null ? <UserRound aria-hidden className="size-4" /> : initialsOf(name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-body-sm font-medium truncate">{shown}</span>
              {post.caption_excerpt !== null && (
                <span className="text-caption text-muted-foreground truncate">
                  {post.caption_excerpt}
                </span>
              )}
            </div>
            <span data-numeric className="text-caption text-muted-foreground hidden sm:inline">
              {commentsLabel(post.comment_count)}
            </span>
            <ScoreChip value={post.score.value} />
            <IconHint label={st2('posts.open', { name: shown })}>
              <Button asChild variant="ghost" size="icon-sm">
                <Link to={publicationPath(props.campaignId, post.post_id)}>
                  <ChevronRight aria-hidden />
                </Link>
              </Button>
            </IconHint>
          </li>
        );
      })}
    </ul>
  );
}

export function PostScoresCard(props: {
  campaignId: string;
  tenantId: string;
  window: Window;
}): ReactNode {
  const query = useRankedPosts(props.campaignId, props.tenantId, {
    window: props.window,
    limit: LISTED_POSTS,
  });
  if (query.status === 'success' && query.data.rows.length === 0) return null;
  return (
    <SentimentCard
      title={st2('posts.title')}
      aside={
        <Button asChild variant="secondary" size="sm">
          <Link to={publicationsPath(props.campaignId)}>{st2('posts.all')}</Link>
        </Button>
      }
    >
      <QueryStates query={query}>
        {(data) => <PostScoresView campaignId={props.campaignId} rows={data.rows} />}
      </QueryStates>
    </SentimentCard>
  );
}
